import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings, imgSrc } from "@/lib/settings";
import { Input } from "@/components/ui/input";
import UploadField from "./UploadField";
import { Plus, Trash2 } from "lucide-react";

export default function GalleryPanel() {
  const { settings, refresh } = useSettings();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => setItems(settings.gallery || []), [settings]);

  const update = (i, key, value) => setItems((list) => list.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  const remove = (i) => setItems((list) => list.filter((_, idx) => idx !== i));
  const add = () => setItems((list) => [...list, { src: "", tag: "Cils", alt: "" }]);

  const save = async () => {
    if (items.some((it) => !it.src)) {
      toast.error("Chaque image doit avoir une photo (URL ou téléversement).");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", { gallery: items });
      await refresh();
      toast.success("Galerie enregistrée — visible sur le site !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10" data-testid="gallery-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-[var(--brand-dark)]">Galerie du site</h2>
          <p className="mt-2 text-sm font-light text-[var(--brand-muted)]">Le filtre de la galerie s'adapte automatiquement aux catégories saisies ici.</p>
        </div>
        <button onClick={add}
                className="flex items-center gap-2 rounded-full bg-[var(--brand-dark)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-95"
                data-testid="gallery-add-btn">
          <Plus size={14} /> Ajouter une image
        </button>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {items.map((it, i) => (
          <div key={i} className="relative border border-black/5 bg-[var(--brand-bg)] p-6" data-testid={`gallery-item-${i}`}>
            {it.src ? <img src={imgSrc(it.src)} alt={it.alt} className="mb-4 h-40 w-full border border-black/5 object-cover" /> : null}
            <UploadField label="Photo" value={it.src} onChange={(v) => update(i, "src", v)} testid={`gallery-src-${i}`} />
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Catégorie</label>
                <Input value={it.tag} onChange={(e) => update(i, "tag", e.target.value)} list="gallery-tags"
                       className="mt-1 h-11 rounded-none border-black/10 bg-transparent text-sm font-light" data-testid={`gallery-tag-${i}`} />
                <datalist id="gallery-tags">
                  {["Cils", "Sourcils", "Tatouage"].map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Description</label>
                <Input value={it.alt} onChange={(e) => update(i, "alt", e.target.value)}
                       className="mt-1 h-11 rounded-none border-black/10 bg-transparent text-sm font-light" data-testid={`gallery-alt-${i}`} />
              </div>
            </div>
            <button onClick={() => remove(i)}
                    className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-[#993333]/30 bg-[var(--brand-bg)] px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#993333] transition-colors hover:bg-[#993333] hover:text-white"
                    data-testid={`gallery-delete-${i}`}>
              <Trash2 size={11} /> Retirer
            </button>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving}
              className="sticky bottom-6 mt-8 rounded-full bg-[var(--brand-dark)] px-10 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] shadow-lg transition-transform duration-300 hover:scale-[0.97] disabled:opacity-50"
              data-testid="gallery-save-btn">
        {saving ? "Enregistrement…" : "Enregistrer la galerie"}
      </button>
    </div>
  );
}
